import './ManagementTemplate.less';
import React, {useState, useEffect} from 'react';
import __ from '../../i18n';
import SegmentedTable from '../../../bootstrap/components/segmented-table/SegmentedTable';
import PagingPanel from '../../../bootstrap/components/paging-panel/PagingPanel';
import Request from '../../../bootstrap/api/Request';
import FilterPanel from '../../../bootstrap/components/filter-panel/FilterPanel';
import Popup from '../../../bootstrap/components/popup/Popup';
import Button from '../../../bootstrap/components/button/Button';
import ConfirmDialog from '../../../bootstrap/components/confirm-dialog/ConfirmDialog';
import Toasts, {TOAST_ERROR, TOAST_SUCCESS} from '../../../bootstrap/scomponents/toasts/Toasts';
import {getUrlHash, setUrlHash} from "../../../bootstrap/utils/url-hash";
import {setSessionItem, getSessionItem} from '../../../bootstrap/utils/session-storage';
import {useLocation} from 'react-router-dom';
import LoadingAlert from "../../components/loading-alert/LoadingAlert";
import DateRangeSelector from "../../../bootstrap/components/date-range-selector/DateRangeSelector";

const DEFAULT_SORTING = {
    key: null,
    isAsc: false
};

const PAGE_SIZES = [20, 50, 100, 200];

export default function ManagementTemplate(
    {
        title,
        TableConfig,
        FilterConfig,
        ListParcel,
        BulkDeleteParcel = null,
        refNames = [],
        defaultSorting = DEFAULT_SORTING,
        allowsSelectItems = true,
        getCreateButtonTitle = () => __('Add new'),
        getDeleteButtonTitle = (count) => __('Delete::count(PLURAL, 1 , ! [ (]:@[)])', {count}),
        getDeleteSuccessMessage = (count) => __('Deleted ::count(PLURAL, 1 1 record, ! :@ records)', {count}),
        getCreateEditor = null,
        getCreateTitle = () => __('Add new'),
        onCreateDone = (newRecord, refreshData) => refreshData(),
    }
) {

    const location = useLocation();

    const stateHashKey = 'state';

    const stateSessionKey = `state:${location.pathname}`;

    const [data, setData] = useState({
        items: [],
        total: 0,
    });

    const getInitialState = () => {
        try {
            return JSON.parse(getUrlHash(stateHashKey));
        } catch (e) {
            return getSessionItem(stateSessionKey, {});
        }
    };

    const initialState = getInitialState();

    const [isLoading, setIsLoading] = useState(true);

    const [filters, setFilters] = useState(initialState.filters || []);

    const [sorting, setSorting] = useState(initialState.sorting || defaultSorting);

    const [pageSize, setPageSize] = useState(initialState.pageSize || PAGE_SIZES[0]);

    const [currentPage, setCurrentPage] = useState(initialState.currentPage || 0);

    const [selectedItemIds, setSelectedItemIds] = useState([]);

    const [deleteConfirmDialogShown, setDeleteConfirmDialogShown] = useState(false);

    const [createEditorShown, setCreateEditorShown] = useState(false);

    const loadItems = () => {
        setIsLoading(true);

        const filtersConfig = FilterConfig.getFiltersConfig();
        const orders = [sorting];
        const limit = pageSize;
        const offset = pageSize * currentPage;

        Request.add(new ListParcel({filters, filtersConfig, orders, limit, offset, refNames}))
            .then(data => {
                setData(data);
            })
            .catch(error => {
                Toasts.add(TOAST_ERROR, error.getMessage());
            })
            .finally(() => {
                setIsLoading(false);
            });

    };

    const pushState = () => {
        const state = {
            filters,
            sorting,
            pageSize,
            currentPage
        };

        setUrlHash(stateHashKey, JSON.stringify(state));

        setSessionItem(stateSessionKey, state);
    };

    useEffect(() => {
        pushState();
        loadItems();
    }, [filters, sorting, currentPage, pageSize]);

    const requestDeleteSelectedItems = () => {
        Request.add(new BulkDeleteParcel(selectedItemIds))
            .then(data => {
                Toasts.add(TOAST_SUCCESS, getDeleteSuccessMessage(data.deletedCount));
                loadItems();
            })
            .catch(error => {
                Toasts.add(TOAST_ERROR, error.getMessage());
            });
    };

    const renderCreateEditor = () => {
        const onDone = (newRecord) => {
            setCreateEditorShown(false);

            onCreateDone(newRecord, refreshData);
        };

        const onCancel = () => {
            setCreateEditorShown(false);
        };

        return getCreateEditor(onDone, onCancel);
    };

    const updateItem = (itemId, values) => {
        setData(data => {
            const item = data.items.find(t => t.id === itemId);

            for (let key in item) {
                if (item.hasOwnProperty(key) && values.hasOwnProperty(key)) {
                    item[key] = values[key];
                }
            }

            return Object.assign({}, data);
        });
    };

    const refreshData = () => {
        loadItems();
    };

    const getColumnConfigs = () => {
        return TableConfig.getColumnConfigs({
            updateItem,
            refreshData,
            refs: data.refs,
        });
    };

    const readyToDelete = BulkDeleteParcel && selectedItemIds.length > 0;

    return <div className="management-template">
        <div className="header">
            <h1 className="title">{title}</h1>
        </div>

        {
            (getCreateEditor || readyToDelete) &&
            <div className="action-bar">
                {
                    getCreateEditor &&
                    !readyToDelete &&
                    <Button
                        title={getCreateButtonTitle()}
                        icon="plus"
                        size="large"
                        appearance="solid-primary"
                        onClick={() => setCreateEditorShown(true)}
                    />
                }
                {
                    readyToDelete &&
                    <Button
                        title={getDeleteButtonTitle(selectedItemIds.length)}
                        icon="trash"
                        size="large"
                        appearance="solid-danger"
                        onClick={() => setDeleteConfirmDialogShown(true)}
                    />
                }
            </div>
        }

        <FilterPanel
            filters={filters}
            filtersConfig={FilterConfig.getFiltersConfig()}
            filterableFields={FilterConfig.getFilterableFields()}
            onChange={setFilters}
        />

        <PagingPanel
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={data.total}
            changePageSize={setPageSize}
            pageSizeOptions={PAGE_SIZES}
            goTo={setCurrentPage}
        />

        <SegmentedTable
            columnConfigs={getColumnConfigs()}
            items={data.items}
            sorting={sorting}
            updateSorting={setSorting}
            selectable={allowsSelectItems}
            selectedItemIds={selectedItemIds}
            updateSelectedItemIds={ids => setSelectedItemIds([...ids])}
            theme="primary"
        />

        {
            !isLoading &&
            data.items.length === 0 &&
            <div className="empty-notify">{__('No data.')}</div>
        }

        <PagingPanel
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={data.total}
            changePageSize={setPageSize}
            pageSizeOptions={PAGE_SIZES}
            goTo={setCurrentPage}
        />

        {
            isLoading &&
            <LoadingAlert/>
        }
        {
            createEditorShown &&
            <Popup
                title={getCreateTitle()}
                close={() => setCreateEditorShown(false)}
            >
                {renderCreateEditor()}
            </Popup>
        }
        {
            deleteConfirmDialogShown &&
            <Popup>
                <ConfirmDialog
                    confirm={() => {
                        setDeleteConfirmDialogShown(false);
                        requestDeleteSelectedItems();
                    }}
                    cancel={() => setDeleteConfirmDialogShown(false)}
                />
            </Popup>
        }
    </div>;
}
